"use client";

import { useNoteEffectStyle } from "@/contexts/studio-context";
import type { NoteEffectStyle } from "@/lib/studio/studio";
import { Group, Line, SectionHeader } from "./atoms";
import { SET } from "./palette";

type EffectOption = {
  id: NoteEffectStyle;
  label: string;
  hint: string;
};

const OPTIONS: readonly EffectOption[] = [
  {
    id: "halo-particles",
    label: "Halo + particules",
    hint: "Halo lumineux et projection d'étincelles vers le haut",
  },
  {
    id: "halo",
    label: "Halo seul",
    hint: "Halo doux pulsé, sans particules",
  },
  {
    id: "none",
    label: "Aucune",
    hint: "Désactiver l'effet visuel",
  },
];

export function AnimationsSection() {
  const [style, setStyle] = useNoteEffectStyle();

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionHeader kicker="Animations" />

      <Group>
        <Line
          withDivider={false}
          label="Effet sur les notes jouées"
          hint="Animation déclenchée à chaque touche enfoncée, au niveau du playhead"
          control={
            <EffectPicker value={style} onChange={setStyle} />
          }
        />
      </Group>
    </section>
  );
}

interface EffectPickerProps {
  value: NoteEffectStyle;
  onChange: (style: NoteEffectStyle) => void;
}

function EffectPicker({ value, onChange }: EffectPickerProps) {
  return (
    <div
      role="radiogroup"
      aria-label="Effet sur les notes jouées"
      style={{ display: "inline-flex", gap: 6 }}
    >
      {OPTIONS.map((option) => {
        const selected = option.id === value;

        return (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={selected}
            title={option.hint}
            onClick={() => onChange(option.id)}
            style={{
              padding: "5px 12px",
              borderRadius: 8,
              border: `1px solid ${selected ? SET.violetGlow : SET.border}`,
              background: selected ? SET.violetSoft : "transparent",
              color: selected ? SET.text : SET.textDim,
              fontSize: 12,
              cursor: "pointer",
              transition: "background 150ms, border-color 150ms, color 150ms",
              boxShadow: selected ? `0 0 8px ${SET.violetGlow}` : "none",
            }}
            onMouseEnter={(e) => {
              if (!selected) {
                e.currentTarget.style.color = SET.text;
                e.currentTarget.style.borderColor = SET.borderHi;
              }
            }}
            onMouseLeave={(e) => {
              if (!selected) {
                e.currentTarget.style.color = SET.textDim;
                e.currentTarget.style.borderColor = SET.border;
              }
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
